<template>
  <div class="stepper-content">
    <!-- 案件コード(検索と追加フォーム) -->
    <template
      v-if="
        contractType === 'CPA_LIMIT_MODULE' &&
        authenticationType !== 'HOMERADIUS'
      "
    >
      <div>
        <app-badge
          item="案件コード"
          msg="・先頭半角英字2文字+半角数字6桁
               ・重複するコードは登録不可"
          placement="end"
        />
      </div>
      <validation-provider
        name="dealCode"
        label="案件コード"
        v-model="dealCode"
        standalone
        :rules="{
          required: true,
          regex: /^[a-zA-Z]{2}[0-9]{6}$/,
          duplicate: { list: cpaNwSetting.dealCodeList },
        }"
        v-slot="{ componentField, meta }"
        ref="provider"
      >
        <v-row>
          <v-col cols="6">
            <app-form-input v-bind="componentField" />
          </v-col>
          <v-col cols="1" v-show="meta.valid">
            <v-btn
              icon="$mdiPlus"
              size="small"
              variant="outlined"
              class="rounded-sm ml-2 mr-3"
              @click="addDealCode"
            />
          </v-col>
        </v-row>
      </validation-provider>

      <!-- 案件コード(一覧) -->
      <v-row class="mb-2">
        <v-col cols="7">
          <app-table
            primary-key="_id"
            :fields="fields"
            :items="dealCodeList"
            bordered
            :per-page="5"
            density="compact"
            stickyHeader="330px"
            :tr-class="() => undefined"
          >
            <template #cell(minus)="{ item }">
              <v-btn
                icon="$mdiMinus"
                size="small"
                variant="outlined"
                class="rounded-sm ma-1"
                @click="removeDealCode(item.dealCode)"
              />
            </template>
          </app-table>
        </v-col>
      </v-row>
      <!-- 単項目として実装可能だが、検査タイミングはcross-validatorと合うようにしたいため、cross-validatorを使用 -->
      <cross-validation-provider
        :passed="isExistDealCode"
        vid="isExistDealCode"
        class="mb-2"
        >案件コードを登録して下さい。</cross-validation-provider
      >
      <!-- 設定アップロードにて重複した案件コードが設定された場合の為のチェック -->
      <cross-validation-provider
        :passed="isDuplicated"
        vid="isDuplicated"
        class="mb-2"
      >
        案件コードが重複しています。
      </cross-validation-provider>
    </template>

    <!-- 折り返し通信 -->
    <v-row v-if="cpaNwOptions.wrapCommunication != null">
      <v-col>
        <app-label-group
          label="折り返し通信"
          :value="
            $filter.enumTo(cpaNwSetting.isWrapCommunication, 'cpaSettingFlg')
          "
        />
      </v-col>
    </v-row>

    <!-- FTP送信 -->
    <v-row v-if="cpaNwOptions.ftpSendSetting != null">
      <v-col>
        <app-label-group
          label="FTP送信"
          :value="$filter.enumTo(cpaNwSetting.isFtpSending, 'cpaSettingFlg')"
        />
      </v-col>
    </v-row>

    <!-- カスコンクライアントIPアドレス -->
    <v-row v-if="cpaNwOptions.cusconClientIp != null">
      <v-col>
        <app-label-group
          label="カスコンクライアントIPアドレス"
          :value="$filter.emptyTo(cpaNwSetting.cusconClientIp, '-')"
        />
      </v-col>
    </v-row>

    <!-- AlwaysON -->
    <v-row v-if="cpaNwOptions.alwaysOn != null">
      <v-col>
        <app-label-group
          label="AlwaysON"
          :value="$filter.enumTo(cpaNwSetting.isAlwaysOn, 'cpaSettingFlg')"
        />
      </v-col>
    </v-row>

    <!-- 長時間呼切断タイマ -->
    <v-row v-if="cpaNwOptions.longTimeCallDisconnectTimer != null">
      <v-col>
        <app-label-group
          label="長時間呼切断タイマ"
          :value="cpaNwSetting.longTimeCallDisconnectTimer"
        />
      </v-col>
    </v-row>

    <v-card
      v-if="
        authenticationType === 'HOMERADIUS' || authenticationType === 'HOME'
      "
      variant="outlined"
      class="mr-2"
    >
      <v-card-text>
        <h3>RADIUS設定項目</h3>
        <!-- Home RADIUS/Accounting Server IPアドレス(Primary) -->
        <v-row>
          <v-col>
            <app-label-group
              label="Home RADIUS/Accounting Server IPアドレス(Primary)"
              :value="
                $filter.emptyTo(
                  cpaNwSetting.radiusSetting!.ipAddressPrimary,
                  '-'
                )
              "
            />
          </v-col>
        </v-row>

        <!-- Home RADIUS/Accounting Server IPアドレス(Secondary) -->
        <v-row>
          <v-col>
            <app-label-group
              label="Home RADIUS/Accounting Server IPアドレス(Secondary)"
              :value="
                $filter.emptyTo(
                  cpaNwSetting.radiusSetting!.ipAddressSecondary,
                  '-'
                )
              "
            />
          </v-col>
        </v-row>

        <!-- Secretkey -->
        <v-row>
          <v-col>
            <app-badge
              item="Secretkey"
              :msg="
                $filter.message('info_sign_num_max', {
                  sign: '. _ ! ? { } + - * / =  % $ & ^ ` ~ | @ ( ) : ; < > [ ]',
                  max: 15,
                })
              "
              placement="end"
            />
            <validation-provider
              name="Secretkey"
              label="Secretkey"
              v-model="cpaNwSetting.radiusSetting!.secretKey"
              :rules="{
                required: true,
                is_cpa_secretkey: true,
                max: { length: 15 },
              }"
              v-slot="{ componentField, errorMessage, meta }"
            >
              <app-form-input
                v-bind="componentField"
                :field-meta="meta"
                :error-messages="errorMessage"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <!-- ドメイン情報送出 -->
        <v-row>
          <v-col>
            <app-badge
              item="ドメイン情報送出"
              :isRequired="false"
              :isInfo="false"
            />
            <v-radio-group
              v-model="cpaNwSetting.radiusSetting!.isDomainInfoSending"
              hide-details
              inline
            >
              <v-radio label="有" :value="true" />
              <v-radio label="無" :value="false" />
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- 課金パケット送出 -->
        <v-row>
          <v-col>
            <app-badge
              item="課金パケット送出"
              :isRequired="false"
              :isInfo="false"
            />
            <v-radio-group
              v-model="cpaNwSetting.radiusSetting!.isBillingPacketSending"
              hide-details
              inline
            >
              <v-radio label="有" :value="true" />
              <v-radio label="無" :value="false" />
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- UDPポート番号(認証/課金) -->
        <v-row>
          <v-col>
            <app-badge
              item="UDPポート番号(認証/課金)"
              :isRequired="false"
              :isInfo="false"
            />
            <v-radio-group
              v-model="cpaNwSetting.radiusSetting!.udpPort"
              hide-details
              inline
            >
              <v-radio label="1645/1646" value="PORT_1645_1646" />
              <v-radio label="1812/1813" value="PORT_1812_1813" />
            </v-radio-group>
          </v-col>
        </v-row>

        <!-- CallingStationID転送 -->
        <v-row v-if="cpaNwOptions.callingStationIdTransfer != null">
          <v-col>
            <app-label-group
              label="CallingStationID転送"
              :value="
                $filter.enumTo(
                  cpaNwSetting.radiusSetting!.callingStationIdTransfer,
                  'cpaCallingStation'
                )
              "
            />
          </v-col>
        </v-row>

        <!-- IMEI転送 -->
        <v-row v-if="cpaNwOptions.imeiTransfer != null">
          <v-col>
            <app-label-group
              label="IMEI転送"
              :value="
                $filter.enumTo(
                  cpaNwSetting.radiusSetting!.isImeiTransfer,
                  'cpaImeiTransfer'
                )
              "
            />
          </v-col>
        </v-row>

        <template v-if="ipAddressType !== 'IPV6'">
          <!-- テストアカウント(注意書き) -->
          <v-row>
            <v-col>
              <app-label-group
                label="テストアカウント"
                value="※テストアカウント情報が正しく入力されていないと障害時にKDDI運用者による試験ができなくなります。"
                style="white-space: nowrap"
              />
            </v-col>
          </v-row>

          <!-- ユーザID -->
          <v-row>
            <v-col>
              <app-badge
                item="ユーザID"
                :is-required="false"
                :msg="`・${$filter.message('info_sign_num_max', {
                  sign: '. - _ /',
                  max: 24,
                })}\n・先頭および末尾での記号の使用は不可`"
                placement="end"
              />
              <validation-provider
                name="testUserId"
                label="ユーザID"
                v-model="cpaNwSetting.radiusSetting!.testAccount!.testUserId"
                :rules="{
                  max: { length: 24 },
                  is_cpa_user_id: true,
                  is_not_both: { notBoth: ['.', '-', '_', '/'] },
                }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-form-input
                  v-bind="componentField"
                  :field-meta="meta"
                  :error-messages="errorMessage"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <!-- PASS -->
          <v-row>
            <v-col>
              <app-badge
                item="PASS"
                :is-required="false"
                :msg="
                  $filter.message('info_sign_num_max', {
                    sign: '. _ ! ? { } + - * / = # % $ & @ ( ) : ; < > [ ]',
                    max: 16,
                  })
                "
                placement="end"
              />
              <validation-provider
                name="testPassword"
                label="PASS"
                v-model="cpaNwSetting.radiusSetting!.testAccount!.testPassword"
                :rules="{
                  is_cpa_pass: true,
                  max: { length: 16 },
                }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-form-input
                  v-bind="componentField"
                  :field-meta="meta"
                  :error-messages="errorMessage"
                />
              </validation-provider>
            </v-col>
          </v-row>

          <!-- テスト用IP -->
          <v-row>
            <v-col>
              <app-badge
                item="テスト用IP"
                :is-required="false"
                :msg="`・IPアドレス/サブネットマスク形式(例:192.168.0.1/32)\n・端末割当アドレスに含まれるホストアドレスで、第4オクテットが1であること`"
                placement="end"
              />
              <validation-provider
                name="testIp"
                label="テスト用IP"
                v-model="cpaNwSetting.radiusSetting!.testAccount!.testIp"
                :rules="{
                  is_ip_subnet: { eq: 32 },
                  is_test_ip: { ipv4List: cpaNwSetting.ueIpAllocationIpv4List },
                }"
                v-slot="{ componentField, errorMessage, meta }"
              >
                <app-form-input
                  v-bind="componentField"
                  :field-meta="meta"
                  :error-messages="errorMessage"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import type { PropType } from "vue";
import type {
  AuthenticationType,
  ContractType,
  CpaNwDetail,
  CpaNwOptions,
  IpAddressType,
} from "@/apis/Cpa5gSaApi";
import { defineComponent } from "vue";
import type { AppTableHeader } from "@/components/AppTable.vue";
// 一覧表示用案件コード
type DealItem = { dealCode: string; _id: number };

export default defineComponent({
  name: "SaNwEditStepper3",
  props: {
    /** 親コンポーネントで保持してるCPA NW情報 */
    value: {
      type: Object as PropType<CpaNwDetail>,
      required: true,
    },
    /** 契約種別 */
    contractType: {
      type: String as PropType<ContractType>,
      required: true,
    },
    /** 認証種別 */
    authenticationType: {
      type: String as PropType<AuthenticationType>,
      required: true,
    },
    /** IPアドレス契約タイプ */
    ipAddressType: {
      type: String as PropType<IpAddressType>,
      required: true,
    },
    /** CPA特殊項目設定 */
    cpaNwOptions: {
      type: Object as PropType<CpaNwOptions>,
      required: true,
    },
  },
  data() {
    return {
      cpaNwSetting: this.value,
      /** 案件コード検索・追加フォーム入力値 */
      dealCode: "",
      /** 案件コードテーブルの列定義 */
      fields: [
        { key: "dealCode", title: "案件コード" },
        { key: "minus", title: "", align: "center" },
      ] as AppTableHeader<DealItem>[],
    };
  },
  computed: {
    /**
     * 案件コードリスト
     * フォームの入力値で検索して部分一致する案件コードを返す
     * オブジェクトの配列を返却(共通テーブルではオブジェクト配列にしなければならないため)
     */
    dealCodeList(): DealItem[] {
      // フォームに入力値があれば、部分一致で検索
      if (this.dealCode) {
        // 空配列で初期化してるのでnullではない
        return this.cpaNwSetting
          .dealCodeList!.map((v, index) => {
            return { dealCode: v, _id: index };
          })
          .filter((v) => v.dealCode.includesIgnoreCase(this.dealCode));
      } else {
        // 入力無しであれば全件返す
        return this.cpaNwSetting.dealCodeList!.map((v, index) => {
          return { dealCode: v, _id: index };
        });
      }
    },
    /**
     * 案件コードが1件以上登録されているかチェック
     * true: 正常 1件以上あり false: エラー 0件
     */
    isExistDealCode(): boolean {
      return this.cpaNwSetting.dealCodeList!.length > 0;
    },
    /**
     * 案件コードの重複チェック
     * 設定アップロードでアップロードした場合、重複した案件コードを入力できるためチェックを実施
     * true: 重複なし false: 重複あり
     */
    isDuplicated(): boolean {
      if (this.cpaNwSetting.dealCodeList !== null) {
        const setElements = new Set(this.cpaNwSetting.dealCodeList);
        return setElements.size === this.cpaNwSetting.dealCodeList.length;
      } else {
        return true;
      }
    },
  },
  methods: {
    /** 案件コードの追加 */
    addDealCode() {
      // 空配列で初期化してるのでnullではない
      this.cpaNwSetting.dealCodeList!.push(this.dealCode);
      // 一覧データに変動があってもバリデーションが作動しないので、処理側でバリデーションを発動させる
      (this.$refs.provider as { validate: () => void }).validate();
    },
    /**
     * 案件コードの削除
     * @param code 案件コード
     */
    removeDealCode(code: string) {
      // 空配列で初期化してるのでnullではない
      this.cpaNwSetting.dealCodeList!.splice(
        this.cpaNwSetting.dealCodeList!.indexOf(code),
        1
      );
      // 一覧データに変動があってもバリデーションが作動しないので、処理側でバリデーションを発動させる
      (this.$refs.provider as { validate: () => void }).validate();
    },
  },
  watch: {
    /** 親からのForm情報更新を反映 */
    value(newValue: CpaNwDetail) {
      this.cpaNwSetting = newValue;
    },
    cpaNwSetting: {
      handler(newValue: CpaNwDetail) {
        this.$emit("input", newValue);
      },
      deep: true,
    },
  },
});
</script>
